import React, { useState, useCallback, useMemo } from 'react'
import { Menu, SemanticCOLORS, Icon } from 'semantic-ui-react'
import classnames from 'classnames'
import get from 'lodash/get'
import ResourceButton from './ResourceButton'
import { useSelector } from '../../../hooks/useSelector'
import usePlayerActions from '../hooks/usePlayerActions'
import PromiseButton from '../../../components/PromiseButton'
import { GameMenu } from './GameMenu'
import { CreditRating } from './CreditRating'
import { BossHeaderChip } from './BossPanel/BossHeaderChip'
import { ResourceHoverOver } from './ResourceButton/ResourceHoverOver'
import { useDispatch } from 'react-redux'
import { setMap, setFullscreen } from '../../../reducers/game/game'
import { TurnTimer } from '../../../components/TurnTimer'
import { QuestsHoverOver } from './ResourceButton/Quests'
import { useGameBoardActions } from '../hooks/useGameBoardActions'
import HelpMenu from './HelpMenu'
import { calculateBalanceSheet } from '@/utils/calculateBalanceSheet'
import { useResourceForecast } from '../hooks/useResourceForecast'
import { useResourceShortageFlash } from '../../../hooks/useResourceShortageFlash'
import { Box, ButtonProps, Card, Cards, Stack, Typography } from '@prometheus/components'
import { findDifficultyPreset } from '@prometheus/utils'
import PromiseButtonV2 from '@/components/PromiseButtonV2'
import { ReadyGate } from '../hooks/useReadyGate'
import { ReadyRequirementOverlay } from './ReadyRequirementOverlay'
import './SheetHeader.scss'

interface SheetHeaderProps {
  /**
   * The ONE gate instance, owned by `GameSheet` and shared with the phone
   * Ready pill. Two independent instances used to keep separate arm/busy
   * state, so a fast header-panel tap plus a FAB tap could fire the same
   * decision twice.
   */
  readyGate: ReadyGate
}

export default ({ readyGate }: SheetHeaderProps) => {
  const [menuOpen, setMenuOpen] = useState<boolean>(false)
  const [helpOpen, setHelpOpen] = useState(false)
  const uid = useSelector(s => s.auth.uid)
  const timerDisabled = useSelector(s => s.game.metadata?.disable_timer)
  const sheet = useSelector(s => s.game.sheet)
  const options = useSelector(s => s.game.options)
  const metadata = useSelector(s => s.game.metadata)
  const difficulty = useSelector(s => s.game.options?.difficulty_settings)
  const ready = useSelector(s => get(s, `game.metadata.players.${uid}.ready`, false))
  const dead = useSelector(s => s.game.metadata?.players[uid || '']?.dead)
  const turn = useSelector(s => get(s, 'game.metadata.turn', 1))
  const is_map = useSelector(s => s.game.galaxy_display)
  const is_fullscreen = useSelector(s => s.game.fullscreen)
  const players = useSelector(s => s.game.metadata?.players) || {}
  const players_choosing_planet = useSelector(s => s.game.metadata?.players_choosing_planet) || []
  const dispatch = useDispatch()
  const rating = useMemo(() => {
    if (!sheet || !options || !metadata) {
      return undefined;
    }
    return calculateBalanceSheet(sheet, options, metadata)
  }, [sheet, options, metadata])
  // Next year only: flash the resource chips the player cannot cover for the
  // coming turn (their mines' production already counted) for a few seconds
  // whenever the turn rolls over.
  const { net_forecast } = useResourceForecast(1)
  const shortages = useResourceShortageFlash(turn, net_forecast)
  const {
    onModifyCashAccounts,
  } = usePlayerActions()
  // Ready no longer dead-ends when the game still wants a travel choice or a
  // trade answer: the gate turns the same button into the way to give one.
  const gateOpen = readyGate.armed && !!readyGate.requirement
  const {
    onPopout,
    poppedOut,
  } = useGameBoardActions()

  const onCancel = useCallback(() => {
    setMenuOpen(false)
  }, [])
  const onMenu = useCallback(() => {
    setMenuOpen(true)
  }, [])
  const onToggleMap = useCallback(() => {
    dispatch(setMap(!is_map))
  }, [dispatch, is_map])
  const onToggleFullscreen = useCallback(() => {
    dispatch(setFullscreen(!is_fullscreen))
  }, [dispatch, is_fullscreen])

  let text = ''
  let color: ButtonProps['variant']
  if (dead) {
    text = 'You are dead'
    color = 'danger'
  } else if (ready) {
    text = 'Readied'
    color = 'primary'
  } else {
    text = 'Ready'
    color = 'warning'
  }

  let message = ''
  if (players_choosing_planet.length && Object.keys(players).length) {
    message += `Waiting for: ${players_choosing_planet.map(p => players[p].name).join(', ')} to pick their move.`
  }

  const result = findDifficultyPreset(difficulty ?? 5)?.key ?? null

  const financeColor = (amt: number) => {
    if (amt > 0) return 'var(--prometheus-primary)'
    if (amt < 0) return 'var(--prometheus-danger)'
    return 'var(--prometheus-text-primary)'
  }

  return (
    <>
      {/* The bar is one stacking context at z-index 1, tied with the resource
          deck and the content panels — so anything hanging out of the Ready
          button gets painted over by both. Lift the whole bar (and the control
          deck within it) only while the gate panel is open, leaving the normal
          layering untouched. */}
      <header className={classnames('sheet-topbar', { 'sheet-topbar--gate-open': gateOpen })}>
      <Card className="sheet-topbar__controls" style={{ position: 'relative' }} rounded={false} padding="none" borderColor="none">
        <Stack direction="row" style={{ justifyContent: 'space-between' }}>
          <Cards direction="row" disableBorders>
            <Card className="sheet-topbar__year" padding="sm" center>
              <Typography variant="h3" retro color="primary">YEAR {turn}</Typography>
            </Card>
            <Card className="sheet-topbar__toggle" onClick={onToggleMap} padding="sm" interactive center>
              <Typography className="sheet-topbar__action">
                <Icon name={is_map ? 'eye slash' : 'eye'} />
                {is_map ? 'Show UI' : 'Hide UI'}
                <kbd>M</kbd>
              </Typography>
            </Card>
            <Card className="sheet-topbar__toggle" onClick={onToggleFullscreen} padding="sm" interactive center>
              <Typography className="sheet-topbar__action">
                <Icon name={is_fullscreen ? 'compress' : 'expand'} />
                {is_fullscreen ? 'Collapse My Planets' : 'Expand My Planets'}
                <kbd>F</kbd>
              </Typography>
            </Card>
            <Card className="sheet-topbar__toggle" onClick={onPopout} padding="sm" interactive center>
              <Typography className="sheet-topbar__action">
                <Icon name={poppedOut ? 'window restore outline' : 'external alternate'} />
                {poppedOut ? 'Restore Gameboard' : 'Popout Gameboard'}
              </Typography>
            </Card>
          </Cards>
          <Cards direction="row" disableBorders>
            {typeof timerDisabled === 'boolean' && !timerDisabled && (
              <TurnTimer />
            )}
            <BossHeaderChip />
            <CreditRating />
            <Card padding="none" center>
              <Box horizontal='base'>
                {/* `ignoreGameLogic` on purpose: PromiseButtonV2 greys every
                    button while anyone is choosing a planet, which is exactly
                    the phase this button now has to stay usable in. Same for
                    an open trade — the gate answers it instead of the button
                    being disabled with the answer two panels away. */}
                <div className="pg-ready-req-anchor">
                  <PromiseButtonV2 size="lg" ignoreGameLogic disabled={dead} onClick={readyGate.onReadyTap} variant={color} outline={!ready}>
                    {text}
                  </PromiseButtonV2>
                  {gateOpen && readyGate.requirement && (
                    <ReadyRequirementOverlay
                      requirement={readyGate.requirement}
                      busy={readyGate.busy}
                      onChoose={readyGate.onChooseTravel}
                      onAuto={readyGate.onAuto}
                      onDismiss={readyGate.onDismiss}
                      align="right"
                    />
                  )}
                </div>
              </Box>
            </Card>
            <Card className="sheet-topbar__menu" padding="sm" interactive center onClick={onMenu}>
              <Icon name="ellipsis vertical" />
            </Card>
          </Cards>
        </Stack>
      </Card>

      <Menu className="sheet-topbar__resources" style={{ zIndex: 1 }}>
        <Menu.Menu position="right">
          <Menu.Item onClick={() => setHelpOpen(true)}>
            <Icon name="info circle" />
            Info & Tips
          </Menu.Item>
          <QuestsHoverOver />
          <ResourceHoverOver />
          <ResourceButton color="blue" label="Wa" flash={shortages.Wa} />
          <ResourceButton color="orange" label="Cu" flash={shortages.Cu} />
          <ResourceButton color="purple" label="Fe" flash={shortages.Fe} />
          <ResourceButton color="grey" label="Ag" flash={shortages.Ag} />
          <ResourceButton color="yellow" label="Au" flash={shortages.Au} />
          <ResourceButton color="olive" label="Oil" flash={shortages.Oil} />
          <ResourceButton color="teal" label="Dia" flash={shortages.Dia} />
          <ResourceButton color="pink" label="Ti" flash={shortages.Ti} />
          <ResourceButton color="violet" label="Pt" flash={shortages.Pt} />
          <ResourceButton color="red" label="U" flash={shortages.U} />
        </Menu.Menu>
      </Menu>
      </header>
      <HelpMenu open={helpOpen} onCancel={() => setHelpOpen(false)} />
      <GameMenu open={menuOpen} onCancel={onCancel} />
    </>
  )
}

